/**
 * src/pages/Anchors.tsx
 *
 * Tab 3 — Anchors. Three sections:
 *
 *   1. Freshness panel (top): per-anchor-source latest_data_ts +
 *      expected cadence + status badge (green / amber / red /
 *      "never"). Red = ≥ 2× expected interval missed (the plan ties
 *      this to the eBay-sold 48h staleness rule once that lands).
 *
 *   2. modelKey lookup: type a modelKey, see the live anchorPrice +
 *      per-source breakdown + a raw median for sanity check (catches
 *      Argos-RRP inflation immediately) + last 50 listings as a
 *      simple price ladder.
 *
 *   3. Missing anchors: top modelKeys with ≥10 listings in 24h but
 *      `anchorPrice: null`. Surfaces cleaner / extraction regressions.
 *
 * Deliberately NOT here (per the PR description): the formula
 * sandbox + the listings-vs-anchor scatter chart. Those need deeper
 * coupling to scoring.service.ts + a chart lib.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, RefreshCw, Search } from 'lucide-react';
import { request } from '@/api/client';
import { cn } from '@/lib/cn';
import { relativeTime, num, txt } from '@/lib/format';

// Inline types — anchors API is small enough we don't need a separate file.
interface FreshnessRow {
  label: string;
  registry_id: string;
  collection: string;
  latest_data_ts: string | null;
  last_run_at: string | null;
  last_run_status: 'ok' | 'blocked' | 'error' | 'never' | null;
  runs_per_day: number | null;
  expected_interval_ms: number | null;
  staleness_ms: number | null;
  status: 'fresh' | 'stale' | 'overdue' | 'never';
}

interface ModelKeyAnchor {
  modelKey: string;
  anchorPrice: number | null;
  anchorSource: string | null;
  anchorConfidence: number | null;
  sources: Record<string, { price: number; confidence: number; sampleCount?: number } | null>;
  raw_median: number | null;
  listing_count: number;
}

interface ModelKeyListing {
  unique_id: string;
  source: string;
  ts: string;
  price: number | null;
  dcs: number | null;
  decision: string | null;
  condition: string | null;
}

interface MissingAnchorRow {
  modelKey: string;
  listing_count_24h: number;
  latest_ts: string | null;
  sources: string[];
}

const STATUS_STYLE: Record<FreshnessRow['status'], string> = {
  fresh:   'bg-emerald-900/40 text-emerald-300 border-emerald-700',
  stale:   'bg-amber-900/40   text-amber-300   border-amber-700',
  overdue: 'bg-rose-900/40    text-rose-300    border-rose-700',
  never:   'bg-slate-800       text-slate-400   border-slate-700',
};

export default function Anchors() {
  return (
    <div className="space-y-4 max-w-7xl">
      <header>
        <h1 className="text-xl font-semibold">Anchors</h1>
        <p className="text-sm text-slate-500 mt-1">
          Anchor freshness · modelKey lookup · missing anchors
        </p>
      </header>

      <FreshnessPanel />
      <ModelKeyLookup />
      <MissingAnchors />
    </div>
  );
}

// ─── Freshness panel ─────────────────────────────────────────────────────────

function FreshnessPanel() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['anchors', 'freshness'],
    queryFn:  () => request<{ ok: true; rows: FreshnessRow[] }>('/v1/observe/dbview/anchors/freshness'),
    refetchInterval: 60_000,
  });

  return (
    <section className="bg-slate-900/40 border border-slate-800 rounded-md p-3">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-semibold tracking-wider text-slate-400 uppercase">
          Anchor freshness
        </h2>
        <button
          onClick={() => refetch()}
          className="text-slate-500 hover:text-white"
          aria-label="Refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {isLoading && <LoadingRow label="Loading freshness…" />}
      {isError   && <ErrorRow   label="Failed to load freshness." />}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {data.rows.map(r => <FreshnessCard key={r.registry_id} row={r} />)}
        </div>
      )}
    </section>
  );
}

function FreshnessCard({ row }: { row: FreshnessRow }) {
  return (
    <div className={cn('border rounded-md p-3 text-xs space-y-1', STATUS_STYLE[row.status])}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-semibold">{row.label}</span>
        <span className="text-[10px] uppercase tracking-wider">{row.status}</span>
      </div>
      <div className="font-mono text-slate-400 text-[10px] truncate">{row.registry_id}</div>
      <div className="space-y-0.5 mt-2">
        <div className="flex justify-between">
          <span className="text-slate-500">latest data</span>
          <span className="font-mono">{row.latest_data_ts ? relativeTime(row.latest_data_ts) : '—'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">last run</span>
          <span className="font-mono">{row.last_run_at ? relativeTime(row.last_run_at) : '—'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">runs/day</span>
          <span className="font-mono">{row.runs_per_day ?? '—'}</span>
        </div>
      </div>
    </div>
  );
}

// ─── modelKey lookup ─────────────────────────────────────────────────────────

function ModelKeyLookup() {
  const [input, setInput]    = useState('');
  const [submitted, setSubmitted] = useState<string | null>(null);

  const anchor = useQuery({
    queryKey: ['anchors', 'mk', submitted],
    queryFn:  () => request<{ ok: true } & ModelKeyAnchor>(
      `/v1/observe/dbview/anchors/${encodeURIComponent(submitted!)}`,
    ),
    enabled: !!submitted,
    retry: false,
  });
  const listings = useQuery({
    queryKey: ['anchors', 'mk-listings', submitted],
    queryFn:  () => request<{ ok: true; rows: ModelKeyListing[] }>(
      `/v1/observe/dbview/anchors/${encodeURIComponent(submitted!)}/listings?limit=50`,
    ),
    enabled: !!submitted,
    retry: false,
  });

  return (
    <section className="bg-slate-900/40 border border-slate-800 rounded-md p-3 space-y-3">
      <h2 className="text-xs font-semibold tracking-wider text-slate-400 uppercase">
        modelKey lookup
      </h2>
      <form
        onSubmit={e => { e.preventDefault(); if (input.trim()) setSubmitted(input.trim()); }}
        className="flex items-end gap-2"
      >
        <label className="flex-1 text-xs text-slate-400 flex flex-col gap-1">
          modelKey
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="iphone_15_pro_max_256gb"
            className="bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-sm font-mono"
          />
        </label>
        <button
          type="submit"
          className="px-3 py-1.5 text-xs font-medium rounded bg-slate-800 hover:bg-slate-700 text-white flex items-center gap-1"
        >
          <Search className="w-3.5 h-3.5" />
          look up
        </button>
      </form>

      {!submitted && (
        <p className="text-xs text-slate-500">
          Enter a modelKey to see its anchor breakdown + last 50 listings.
        </p>
      )}

      {submitted && anchor.isLoading && <LoadingRow label="Looking up…" />}
      {submitted && anchor.isError && (
        <div className="text-xs text-rose-300">
          {(anchor.error as Error)?.message?.includes('404')
            ? 'No deals match that modelKey.'
            : 'Lookup failed.'}
        </div>
      )}
      {anchor.data && <ModelKeyBreakdown anchor={anchor.data} />}
      {listings.data && <ListingsTable listings={listings.data.rows} />}
    </section>
  );
}

function ModelKeyBreakdown({ anchor }: { anchor: ModelKeyAnchor }) {
  const sourceRows = Object.entries(anchor.sources)
    .filter(([, v]) => v !== null) as Array<[string, { price: number; confidence: number; sampleCount?: number }]>;
  const sanityFlag = anchor.raw_median !== null && anchor.anchorPrice !== null
    && Math.abs(anchor.anchorPrice - anchor.raw_median) > anchor.raw_median * 0.4;

  return (
    <div className="bg-slate-950/40 border border-slate-800 rounded p-3 text-xs space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1">
        <Stat label="modelKey"    value={txt(anchor.modelKey)} mono />
        <Stat label="anchor £"    value={num(anchor.anchorPrice)} />
        <Stat label="confidence"  value={num(anchor.anchorConfidence, 2)} />
        <Stat label="listings"    value={anchor.listing_count.toLocaleString()} />
      </div>

      <div>
        <div className="text-slate-500 mb-1">Per-source breakdown</div>
        {sourceRows.length === 0
          ? <div className="text-slate-500">No source breakdown stored on the latest deal.</div>
          : (
            <table className="w-full">
              <thead className="text-slate-500">
                <tr>
                  <th className="text-left  font-medium px-2 py-1">source</th>
                  <th className="text-right font-medium px-2 py-1">£</th>
                  <th className="text-right font-medium px-2 py-1">conf</th>
                  <th className="text-right font-medium px-2 py-1">samples</th>
                </tr>
              </thead>
              <tbody>
                {sourceRows.map(([k, v]) => (
                  <tr key={k} className="border-t border-slate-800">
                    <td className="px-2 py-1 font-mono text-slate-200">{k}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-slate-300">{num(v.price)}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-slate-400">{num(v.confidence, 2)}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-slate-400">{v.sampleCount ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      <div className="flex items-center justify-between gap-2">
        <span className="text-slate-500">raw median (sanity)</span>
        <span className={cn(
          'font-mono tabular-nums',
          sanityFlag ? 'text-amber-400' : 'text-slate-300',
        )}>
          {anchor.raw_median === null ? '—' : anchor.raw_median.toFixed(0)}
          {sanityFlag && <span className="ml-2 text-[10px] uppercase">⚠ diverges from anchor</span>}
        </span>
      </div>
    </div>
  );
}

function ListingsTable({ listings }: { listings: ModelKeyListing[] }) {
  if (listings.length === 0) {
    return <div className="text-xs text-slate-500">No listings.</div>;
  }
  return (
    <div className="overflow-x-auto border border-slate-800 rounded">
      <table className="w-full text-xs">
        <thead className="bg-slate-900/80 text-slate-400">
          <tr>
            <th className="text-left  px-2 py-1.5">when</th>
            <th className="text-left  px-2 py-1.5">source</th>
            <th className="text-right px-2 py-1.5">£</th>
            <th className="text-right px-2 py-1.5">DCS</th>
            <th className="text-left  px-2 py-1.5">decision</th>
            <th className="text-left  px-2 py-1.5">condition</th>
          </tr>
        </thead>
        <tbody>
          {listings.map(l => (
            <tr key={l.unique_id} className="border-t border-slate-800">
              <td className="px-2 py-1.5 text-slate-400">{relativeTime(l.ts)}</td>
              <td className="px-2 py-1.5 font-mono text-slate-200">{l.source}</td>
              <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">
                {num(l.price)}
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">
                {num(l.dcs)}
              </td>
              <td className="px-2 py-1.5 font-mono text-slate-300">{l.decision ?? '—'}</td>
              <td className="px-2 py-1.5 text-slate-400">{txt(l.condition)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Missing anchors ─────────────────────────────────────────────────────────

function MissingAnchors() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['anchors', 'missing'],
    queryFn:  () => request<{ ok: true; rows: MissingAnchorRow[] }>(
      '/v1/observe/dbview/anchors/missing?minListings=10',
    ),
    refetchInterval: 60_000,
  });

  return (
    <section className="bg-slate-900/40 border border-slate-800 rounded-md p-3">
      <h2 className="text-xs font-semibold tracking-wider text-slate-400 uppercase mb-2">
        Missing anchors (≥10 listings in 24h, no anchorPrice)
      </h2>
      {isLoading && <LoadingRow label="Loading missing anchors…" />}
      {isError   && <ErrorRow   label="Failed to load." />}
      {data && (data.rows.length === 0
        ? <div className="text-xs text-slate-500">All modelKeys with sufficient volume are anchored. ✓</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-500">
                <tr>
                  <th className="text-left  px-2 py-1 font-medium">modelKey</th>
                  <th className="text-right px-2 py-1 font-medium">listings 24h</th>
                  <th className="text-left  px-2 py-1 font-medium">sources</th>
                  <th className="text-left  px-2 py-1 font-medium">latest</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map(r => (
                  <tr key={r.modelKey} className="border-t border-slate-800">
                    <td className="px-2 py-1 font-mono text-slate-200">{txt(r.modelKey)}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-amber-400">
                      {r.listing_count_24h.toLocaleString()}
                    </td>
                    <td className="px-2 py-1 text-slate-300 font-mono">{r.sources.join(', ')}</td>
                    <td className="px-2 py-1 text-slate-400">{r.latest_ts ? relativeTime(r.latest_ts) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </section>
  );
}

// ─── small bits ──────────────────────────────────────────────────────────────

function Stat({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col">
      <span className="text-slate-500">{label}</span>
      <span className={cn('text-slate-200 truncate', mono && 'font-mono')}>{value}</span>
    </div>
  );
}

function LoadingRow({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-slate-500 py-3">
      <Loader2 className="w-4 h-4 animate-spin" />
      {label}
    </div>
  );
}

function ErrorRow({ label }: { label: string }) {
  return <div className="text-sm text-rose-300 py-3">{label}</div>;
}
